import { useCallback, useEffect, useState } from "react";
import { Link } from "react-router-dom";
import client, { API } from "@/lib/api";
import { Buildings, ClipboardText, FilePdf, ArrowRight, Image as ImageIcon } from "@phosphor-icons/react";
import { toast } from "sonner";

const STATUS_STYLE = {
  Scheduled: "bg-[#FEF3C7] text-[#92400E] border-[#FBBF24]",
  "In Progress": "bg-[#DBEAFE] text-[#1E40AF] border-[#60A5FA]",
  Completed: "bg-[#D1FAE5] text-[#065F46] border-[#34D399]",
};

export function CustomerDashboard() {
  const [insp, setInsp] = useState([]);
  const [props, setProps] = useState([]);
  const load = useCallback(async () => {
    const [i, p] = await Promise.all([
      client.get("/customer/inspections"),
      client.get("/customer/properties"),
    ]);
    setInsp(i.data);
    setProps(p.data);
  }, []);
  useEffect(() => { load(); }, [load]);

  const byType = (t) => insp.filter((x) => x.inspection_type === t).length;

  return (
    <div data-testid="customer-dashboard">
      <div className="text-[11px] uppercase tracking-[0.3em] text-[#64748B] mb-1">Customer portal</div>
      <h1 className="trent-heading text-3xl md:text-4xl font-black tracking-tighter text-[#0F172A] mb-8">
        Welcome back
      </h1>

      <div className="grid grid-cols-2 md:grid-cols-5 gap-4">
        <Stat label="Properties" value={props.length} testId="stat-properties" />
        <Stat label="Reports" value={insp.length} testId="stat-reports" />
        <Stat label="Move-In" value={byType("Move-In")} testId="stat-movein" />
        <Stat label="Move-Out" value={byType("Move-Out")} testId="stat-moveout" />
        <Stat label="Mid-Term" value={byType("Mid-Term")} testId="stat-midterm" />
      </div>

      <div className="mt-8 bg-white border border-[#E5E7EB] rounded-md overflow-hidden">
        <div className="px-4 py-3 bg-[#F1F5F9] text-[10px] uppercase tracking-[0.25em] text-[#64748B] font-semibold">
          Latest reports
        </div>
        <table className="w-full text-sm">
          <thead className="bg-[#F1F5F9] text-[10px] uppercase tracking-[0.2em] text-[#64748B]">
            <tr>
              <th className="text-left px-4 py-3">Property</th>
              <th className="text-left px-4 py-3 hidden md:table-cell">Type</th>
              <th className="text-left px-4 py-3">Date</th>
              <th className="text-left px-4 py-3">Status</th>
              <th className="px-4 py-3 w-32 text-right"></th>
            </tr>
          </thead>
          <tbody>
            {insp.length === 0 && (
              <tr><td colSpan={5} className="text-center text-[#64748B] py-10" data-testid="dashboard-empty">
                No documents or reports have been assigned to your account yet. Please contact your administrator.
              </td></tr>
            )}
            {insp.slice(0, 8).map((i) => (
              <tr key={i.id} className="border-t border-[#E5E7EB]" data-testid={`cust-inspection-${i.id}`}>
                <td className="px-4 py-3 font-semibold">{i.property?.address || "—"}</td>
                <td className="px-4 py-3 hidden md:table-cell">{i.inspection_type}</td>
                <td className="px-4 py-3 text-[#475569]">{i.inspection_date}</td>
                <td className="px-4 py-3">
                  <span className={`condition-pill ${STATUS_STYLE[i.status] || ""}`}>{i.status}</span>
                </td>
                <td className="px-4 py-3 text-right">
                  <Link to={`/customer-dashboard/inspections/${i.id}`} className="text-xs font-semibold uppercase tracking-wider text-[#0F172A] hover:text-[#16A34A] inline-flex items-center gap-1">
                    Open <ArrowRight size={12} weight="bold" />
                  </Link>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
}

const Stat = ({ label, value, testId }) => (
  <div className="bg-white border border-[#E5E7EB] rounded-md p-5" data-testid={testId}>
    <div className="text-[10px] uppercase tracking-[0.25em] text-[#64748B]">{label}</div>
    <div className="trent-heading text-3xl font-black text-[#0F172A] mt-1">{value}</div>
  </div>
);

export function CustomerProperties() {
  const [items, setItems] = useState([]);
  useEffect(() => { client.get("/customer/properties").then((r) => setItems(r.data)); }, []);
  return (
    <div data-testid="customer-properties-page">
      <div className="text-[11px] uppercase tracking-[0.3em] text-[#64748B] mb-1">Customer portal</div>
      <h1 className="trent-heading text-3xl md:text-4xl font-black tracking-tighter text-[#0F172A] mb-6">My Properties</h1>
      {items.length === 0 && <div className="text-[#64748B] text-center py-12 border border-dashed border-[#E5E7EB] rounded-md" data-testid="properties-empty">No documents or reports have been assigned to your account yet. Please contact your administrator.</div>}
      <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-4">
        {items.map((p) => (
          <div key={p.id} className="bg-white border border-[#E5E7EB] rounded-md p-5" data-testid={`cust-prop-${p.id}`}>
            <div className="w-10 h-10 bg-[#0F172A] flex items-center justify-center rounded-sm text-white">
              <Buildings size={18} weight="duotone" />
            </div>
            <div className="mt-3 font-semibold text-[#0F172A] leading-snug">{p.address}</div>
            <div className="text-xs text-[#64748B] mt-1">{p.eircode || "—"} · {p.property_type}</div>
            <div className="mt-3 flex flex-wrap gap-2 text-[10px] uppercase tracking-wider">
              <span className="border border-[#E5E7EB] px-2 py-0.5 rounded">{p.bedrooms} bed</span>
              <span className="border border-[#E5E7EB] px-2 py-0.5 rounded">{p.bathrooms} bath</span>
              {p.ber_rating && <span className="border border-[#86EFAC] bg-[#DCFCE7] text-[#14532D] px-2 py-0.5 rounded">BER {p.ber_rating}</span>}
            </div>
          </div>
        ))}
      </div>
    </div>
  );
}

export function CustomerInspectionsList() {
  const [items, setItems] = useState([]);
  useEffect(() => { client.get("/customer/inspections").then((r) => setItems(r.data)); }, []);
  const groups = ["Move-In", "Move-Out", "Mid-Term", "Video Walk-Around"];
  return (
    <div data-testid="customer-inspections-page">
      <div className="text-[11px] uppercase tracking-[0.3em] text-[#64748B] mb-1">Customer portal</div>
      <h1 className="trent-heading text-3xl md:text-4xl font-black tracking-tighter text-[#0F172A] mb-6">Inspection Reports</h1>
      {items.length === 0 && (
        <div className="text-[#64748B] text-center py-12 border border-dashed border-[#E5E7EB] rounded-md" data-testid="inspections-empty">
          No documents or reports have been assigned to your account yet. Please contact your administrator.
        </div>
      )}
      {groups.map((g) => {
        const list = items.filter((i) => i.inspection_type === g);
        if (!list.length) return null;
        return (
          <div key={g} className="mb-8">
            <h2 className="trent-heading text-lg font-bold text-[#0F172A] mb-3">{g} reports</h2>
            <div className="bg-white border border-[#E5E7EB] rounded-md overflow-hidden">
              <table className="w-full text-sm">
                <thead className="bg-[#F1F5F9] text-[10px] uppercase tracking-[0.2em] text-[#64748B]">
                  <tr>
                    <th className="text-left px-4 py-3">Property</th>
                    <th className="text-left px-4 py-3">Date</th>
                    <th className="text-left px-4 py-3">Status</th>
                    <th className="px-4 py-3 w-32 text-right"></th>
                  </tr>
                </thead>
                <tbody>
                  {list.map((i) => (
                    <tr key={i.id} className="border-t border-[#E5E7EB]">
                      <td className="px-4 py-3 font-semibold">{i.property?.address || "—"}</td>
                      <td className="px-4 py-3 text-[#475569]">{i.inspection_date}</td>
                      <td className="px-4 py-3">
                        <span className={`condition-pill ${STATUS_STYLE[i.status] || ""}`}>{i.status}</span>
                      </td>
                      <td className="px-4 py-3 text-right">
                        <Link to={`/customer-dashboard/inspections/${i.id}`} className="text-xs font-semibold uppercase tracking-wider text-[#0F172A] hover:text-[#16A34A] inline-flex items-center gap-1">
                          Open <ArrowRight size={12} weight="bold" />
                        </Link>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        );
      })}
    </div>
  );
}

export function CustomerInspectionDetail() {
  const id = window.location.pathname.split("/").pop();
  const [i, setI] = useState(null);
  const [msg, setMsg] = useState("");
  const [sending, setSending] = useState(false);

  useEffect(() => { client.get(`/customer/inspections/${id}`).then((r) => setI(r.data)); }, [id]);

  const download = async () => {
    const token = localStorage.getItem("trent_token");
    const r = await fetch(`${API}/customer/inspections/${id}/report`, { headers: { Authorization: `Bearer ${token}` } });
    const blob = await r.blob();
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url; a.download = `trent-report-${id}.pdf`;
    document.body.appendChild(a); a.click(); a.remove(); URL.revokeObjectURL(url);
  };

  const requestCorrection = async () => {
    if (!msg.trim()) { toast.error("Please describe the correction needed."); return; }
    setSending(true);
    try {
      await client.post(`/customer/inspections/${id}/request_correction`, { message: msg });
      toast.success("Request sent to admin.");
      setMsg("");
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Failed to send request.");
    } finally { setSending(false); }
  };

  if (!i) return <div className="text-[#64748B]">Loading…</div>;
  const sig = i.signature_status || { tenant: {}, landlord: {} };

  return (
    <div data-testid="customer-inspection-detail">
      <Link to="/customer-dashboard/inspections" className="text-xs font-semibold uppercase tracking-wider text-[#64748B] hover:text-[#0F172A] inline-flex items-center gap-1 mb-3">
        ← Back to reports
      </Link>
      <h1 className="trent-heading text-3xl font-black tracking-tighter text-[#0F172A] mb-1">{i.property?.address}</h1>
      <div className="text-sm text-[#475569] mb-6">{i.inspection_type} · {i.inspection_date} · Inspector: {i.inspector_name}</div>

      <div className="grid md:grid-cols-3 gap-4 mb-6">
        <Stat label="Status" value={i.status} testId="detail-status" />
        <Stat label="Tenant signature" value={sig.tenant?.status || "Pending"} testId="detail-sig-tenant" />
        <Stat label="Landlord signature" value={sig.landlord?.status || "Pending"} testId="detail-sig-landlord" />
      </div>

      <div className="bg-white border border-[#E5E7EB] rounded-md p-6 mb-6">
        <h2 className="trent-heading text-lg font-bold text-[#0F172A] mb-3">PDF report</h2>
        <p className="text-sm text-[#475569] mb-4">
          Download the signed inspection report for your records.
        </p>
        <button onClick={download} className="trent-btn-primary" data-testid="cust-download-pdf">
          <FilePdf size={16} /> Download PDF report
        </button>
      </div>

      {Array.isArray(i.videos) && i.videos.length > 0 && (
        <div className="bg-white border border-[#E5E7EB] rounded-md p-6 mb-6" data-testid="cust-videos-block">
          <h2 className="trent-heading text-lg font-bold text-[#0F172A] mb-3">
            Walk-around videos ({i.videos.length})
          </h2>
          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-3">
            {i.videos.map((v) => (
              <div key={v.id} className="border border-[#E5E7EB] rounded-md overflow-hidden bg-black">
                <video src={v.data_url} controls className="w-full h-44 object-cover bg-black" preload="metadata">
                  <track kind="captions" />
                </video>
                <div className="bg-white px-3 py-2 text-xs text-[#475569]">
                  {v.label || "Video"}
                </div>
              </div>
            ))}
          </div>
        </div>
      )}

      {Array.isArray(i.photos) && i.photos.length > 0 && (
        <div className="bg-white border border-[#E5E7EB] rounded-md p-6 mb-6">
          <h2 className="trent-heading text-lg font-bold text-[#0F172A] mb-3 flex items-center gap-2">
            <ImageIcon size={18} weight="duotone" /> Property photos ({i.photos.length})
          </h2>
          <div className="grid grid-cols-2 md:grid-cols-4 lg:grid-cols-5 gap-3">
            {i.photos.slice(0, 20).map((p) => (
              <div key={p.id} className="relative">
                <img src={p.data_url} alt={p.category} className="w-full h-28 object-cover rounded-sm border border-[#E5E7EB]" />
                <div className="absolute bottom-0 left-0 right-0 bg-black/60 text-white text-[10px] uppercase tracking-widest px-2 py-1">
                  {p.category}
                </div>
              </div>
            ))}
          </div>
        </div>
      )}

      <div className="bg-white border border-[#E5E7EB] rounded-md p-6">
        <h2 className="trent-heading text-lg font-bold text-[#0F172A] mb-3">Request a correction</h2>
        <p className="text-sm text-[#475569] mb-3">
          See something inaccurate? Send a message to your administrator — they'll review and respond by email.
        </p>
        <textarea
          rows={4}
          value={msg}
          onChange={(e) => setMsg(e.target.value)}
          className="trent-input w-full"
          placeholder="Describe what needs to be corrected…"
          data-testid="cust-correction-input"
        />
        <button onClick={requestCorrection} disabled={sending} className="trent-btn-secondary mt-3" data-testid="cust-correction-submit">
          {sending ? "Sending…" : "Send to admin"}
        </button>
      </div>
    </div>
  );
}

export function CustomerDocuments() {
  const [docs, setDocs] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    client.get("/customer/documents")
      .then((r) => setDocs(r.data))
      .finally(() => setLoading(false));
  }, []);

  const download = async (d) => {
    const { data } = await client.get(`/customer/documents/${d.id}`);
    const a = document.createElement("a");
    a.href = data.data_url;
    a.download = d.name;
    document.body.appendChild(a); a.click(); a.remove();
  };

  return (
    <div data-testid="customer-documents-page">
      <div className="text-[11px] uppercase tracking-[0.3em] text-[#64748B] mb-1">Customer portal</div>
      <h1 className="trent-heading text-3xl md:text-4xl font-black tracking-tighter text-[#0F172A] mb-6">My Documents</h1>
      <p className="text-sm text-[#475569] mb-6 max-w-prose">
        Documents your property manager has shared with you — leases, deposit receipts,
        ID verifications, and more. Click any file to download a copy.
      </p>

      {loading ? (
        <div className="text-[#64748B] text-sm">Loading…</div>
      ) : docs.length === 0 ? (
        <div className="text-[#64748B] text-center py-12 border border-dashed border-[#E5E7EB] rounded-md" data-testid="documents-empty">
          No documents or reports have been assigned to your account yet. Please contact your administrator.
        </div>
      ) : (
        <div className="bg-white border border-[#E5E7EB] rounded-md divide-y divide-[#E5E7EB]">
          {docs.map((d) => (
            <button
              key={d.id}
              onClick={() => download(d)}
              className="w-full text-left p-4 flex items-center justify-between hover:bg-[#F8FAFC]"
              data-testid={`cust-doc-${d.id}`}
            >
              <div className="flex items-center gap-3 min-w-0">
                <div className="w-10 h-10 bg-[#0F172A] rounded-sm flex items-center justify-center text-white shrink-0">
                  <FilePdf size={18} weight="duotone" />
                </div>
                <div className="min-w-0">
                  <div className="font-semibold truncate">{d.name}</div>
                  <div className="text-xs text-[#64748B]">
                    {d.category}{d.label ? ` · ${d.label}` : ""} · shared {d.created_at?.slice(0, 10)}
                  </div>
                </div>
              </div>
              <span className="text-[10px] uppercase tracking-wider text-[#16A34A] font-semibold">Download</span>
            </button>
          ))}
        </div>
      )}
    </div>
  );
}

export function CustomerAccount() {
  const [deleting, setDeleting] = useState(false);
  const exportData = async () => {
    const r = await client.get("/me/export");
    const blob = new Blob([JSON.stringify(r.data, null, 2)], { type: "application/json" });
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url; a.download = `trent-export-${new Date().toISOString().slice(0, 10)}.json`;
    document.body.appendChild(a); a.click(); a.remove(); URL.revokeObjectURL(url);
    toast.success("Your data has been exported.");
  };
  const requestDeletion = async () => {
    if (!window.confirm("This sends a deletion request to admin. Continue?")) return;
    setDeleting(true);
    try {
      await client.post("/me/delete_request", { reason: "User-initiated GDPR deletion request" });
      toast.success("Deletion request received. Admin will process within 30 days.");
    } catch { toast.error("Could not submit request."); }
    finally { setDeleting(false); }
  };
  return (
    <div data-testid="customer-account-page">
      <div className="text-[11px] uppercase tracking-[0.3em] text-[#64748B] mb-1">Customer portal</div>
      <h1 className="trent-heading text-3xl md:text-4xl font-black tracking-tighter text-[#0F172A] mb-6">Account & data</h1>

      <div className="bg-white border border-[#E5E7EB] rounded-md p-6 mb-6">
        <h2 className="trent-heading text-lg font-bold text-[#0F172A] mb-2">Export my data (GDPR Article 20)</h2>
        <p className="text-sm text-[#475569] mb-4">Download a copy of every record we hold about you — properties, inspections, photos, signatures, correction requests.</p>
        <button onClick={exportData} className="trent-btn-primary" data-testid="export-data-btn">Export my data</button>
      </div>

      <div className="bg-white border border-[#FCA5A5] rounded-md p-6">
        <h2 className="trent-heading text-lg font-bold text-[#991B1B] mb-2">Request data deletion (GDPR Article 17)</h2>
        <p className="text-sm text-[#475569] mb-4">
          Submit a request to permanently delete your account and all personal data. Admin will process within 30 days. Records that must be retained by law (RTB evidentiary period — typically 6 years) will be anonymised instead of deleted.
        </p>
        <button onClick={requestDeletion} disabled={deleting} className="trent-btn-secondary text-[#991B1B] border-[#FCA5A5] hover:bg-[#FEE2E2]" data-testid="delete-request-btn">
          {deleting ? "Sending…" : "Request data deletion"}
        </button>
      </div>
    </div>
  );
}
